import { DOCUMENT } from '@angular/common';
import { Injectable, inject, signal } from '@angular/core';

/** Temas soportados por el sistema de diseno liquid glass. */
export type KsTheme = 'dark' | 'light';

/** Clave de persistencia. La comparte el script anti-FOUC de `index.html`. */
const STORAGE_KEY = 'ks-theme';

/**
 * Gobierna el tema de la consola.
 *
 * El atributo `data-theme` del `<html>` es la unica fuente de verdad: los
 * tokens `--ks-*` cuelgan de el. El script inline de `index.html` ya lo dejo
 * escrito antes del primer pintado, asi que aqui solo lo leemos y lo mutamos.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly document = inject(DOCUMENT);

  private readonly themeSignal = signal<KsTheme>(this.readInitialTheme());

  /** Tema activo. Solo lectura para los consumidores. */
  readonly theme = this.themeSignal.asReadonly();

  /**
   * Cache de valores resueltos de las CSS vars, indexada por tema.
   * Se invalida completa al cambiar de tema porque TODOS los tokens cambian.
   */
  private readonly tokenCache = new Map<KsTheme, Map<string, string>>();

  constructor() {
    // Reafirma el atributo por si el script anti-FOUC no llego a correr.
    this.applyTheme(this.themeSignal());
  }

  /** Alterna entre oscuro y claro y persiste la eleccion. */
  toggle(): void {
    this.setTheme(this.themeSignal() === 'dark' ? 'light' : 'dark');
  }

  /** Fija el tema, lo escribe en el DOM y lo persiste. */
  setTheme(theme: KsTheme): void {
    this.themeSignal.set(theme);
    this.applyTheme(theme);
    this.tokenCache.delete(theme);

    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // Modo privado o almacenamiento bloqueado: el tema vive solo en memoria.
    }
  }

  /**
   * Resuelve el valor computado de una CSS var del tema activo.
   *
   * Hace falta porque un `<canvas>` (ECharts, sparklines) NO hereda custom
   * properties: hay que pasarle el color ya resuelto en JavaScript.
   *
   * @param name Nombre del token, con o sin `--` (p. ej. `--ks-accent`).
   * @param fallback Valor a devolver si el token no existe o no hay DOM.
   */
  token(name: string, fallback = ''): string {
    const variable = name.startsWith('--') ? name : `--${name}`;
    const theme = this.themeSignal();

    let cache = this.tokenCache.get(theme);
    if (!cache) {
      cache = new Map<string, string>();
      this.tokenCache.set(theme, cache);
    }

    const cached = cache.get(variable);
    if (cached !== undefined) {
      return cached;
    }

    const root = this.document.documentElement;
    const value = root ? getComputedStyle(root).getPropertyValue(variable).trim() : '';
    const resolved = value || fallback;
    cache.set(variable, resolved);

    return resolved;
  }

  /** Resuelve varios tokens de una sola pasada. Util al armar un tema de ECharts. */
  tokens(names: string[]): Record<string, string> {
    return names.reduce<Record<string, string>>((acc, name) => {
      acc[name] = this.token(name);
      return acc;
    }, {});
  }

  private applyTheme(theme: KsTheme): void {
    this.document.documentElement.setAttribute('data-theme', theme);
  }

  private readInitialTheme(): KsTheme {
    const fromDom = this.document.documentElement.getAttribute('data-theme');
    if (fromDom === 'light' || fromDom === 'dark') {
      return fromDom;
    }

    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === 'light' || stored === 'dark') {
        return stored;
      }
    } catch {
      // Ignorado: se cae al media query.
    }

    const prefersLight =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-color-scheme: light)').matches;

    return prefersLight ? 'light' : 'dark';
  }
}
